/***
 * @View
 *
 * @name common
 * @type common
 * @description Common (Base Block) - Properties and objects that apply to all blocks.
 * @maturing
 * @content
 *    @prop @hidden @required @readonly type type={string} title="Type"
 *    @prop class type={string} title="Class" description="This class is for the main block."
 *    @prop @advanced tabindex type={string} title="Tab Index" description="Attribute that is used to manage keyboard focus as well as the sequential keyboard navigation (tab order)."
 *    @prop @advanced order type={integer} title="Order" description="Sets the order of the blocks and keeps them in a loaded state until their order comes up. Ordered blocks can be specified out of order and in any numbered sequence. They can also be nested inside unordered blocks. Note: An ordered block will still propagate its order to its children.  A special order state, 'end', only activates once all ordered/unordered blocks are completed."
 *    @prop @advanced disable_order_on_replay type={boolean} title="Disable Order on Replay - Disables the previous assigned block order when returning to a completed page."
 *    @prop @advanced animation type={string} title="Animation" description="Click and mouseover animation javascript (AniJS) options." @allow animationPicker
 *    @contains @advanced @object_of 'onscroll' with:
 *      @prop [animation] type={string} title="Scroll Animation Style" description="Choose one of several options to either fade, flip, slide or zoom for the scroll animation."
 *        "fade"
 *        "fade-up"
 *        "fade-down"
 *        "fade-left"
 *        "fade-right"
 *        "fade-up-right"
 *        "fade-up-left"
 *        "fade-down-right"
 *        "fade-down-left"
 *        "flip-up"
 *        "flip-down"
 *        "flip-left"
 *        "flip-right"
 *        "slide-up"
 *        "slide-down"
 *        "slide-left"
 *        "slide-right"
 *        "zoom-in"
 *        "zoom-in-up"
 *        "zoom-in-down"
 *        "zoom-in-left"
 *        "zoom-in-right"
 *        "zoom-out"
 *        "zoom-out-up"
 *        "zoom-out-down"
 *        "zoom-out-left"
 *        "zoom-out-right"
 *      @prop duration type={string} title="Scroll Animation Duration" description="Milliseconds (e.g., '500'); value from 0 to 3000, with step 50ms." pattern="^[1-3]?[0-9]?[50]?0$"
 *      @prop delay type={string} title="Scroll Animation Delay" description="Milliseconds (e.g., '500'); value from 0 to 3000, with step 50ms." pattern="^[1-3]?[0-9]?[50]?0$"
 *      @prop offset type={string} title="Scroll Animation Offset" description="Pixels offset from the original trigger point (e.g., '50')" pattern="^[0-9]*$"
 *      @prop mirror type={boolean} title="Scroll Animation Mirror - Reverse the scroll animation properties."
 *      @prop once type={boolean} title="Scroll Animation Once - Animation only occurs one time."
 *      @prop [easing] type={string} title="Scroll Animation Easing" description="Choose the acceleration or deceleration style for the scroll animation."
 *        "ease"
 *        "ease-in"
 *        "ease-out"
 *        "ease-in-out"
 *        "ease-in-back"
 *        "ease-out-back"
 *        "ease-in-out-back"
 *        "ease-in-sine"
 *        "ease-out-sine"
 *        "ease-in-out-sine"
 *        "ease-in-quad"
 *        "ease-out-quad"
 *        "ease-in-out-quad"
 *        "ease-in-cubic"
 *        "ease-out-cubic"
 *        "ease-in-out-cubic"
 *        "ease-in-quart"
 *        "ease-out-quart"
 *        "ease-in-out-quart"
 *    @end
 *    @contains @advanced @object_of 'aria' with:
 *      @prop [role] type={string} title="Role" description="Defines a user interface element like a button or a tooltip. Roles are categorized as abstract, widget, document structure and landmark roles."
 *        "alert"
 *        "alertdialog"
 *        "button"
 *        "checkbox"
 *        "dialog"
 *        "gridcell"
 *        "ink"
 *        "log"
 *        "marquee"
 *        "menuitem"
 *        "menuitemcheckbox"
 *        "menuitemradio"
 *        "option"
 *        "progressbar"
 *        "radio"
 *        "scrollbar"
 *        "slider"
 *        "spinbutton"
 *        "status"
 *        "tab"
 *        "tabpanel"
 *        "textbox"
 *        "timer"
 *        "tooltip"
 *        "treeitem"
 *        "combobox"
 *        "grid"
 *        "listbox"
 *        "menu"
 *        "menubar"
 *        "radiogroup"
 *        "tablist"
 *        "tree"
 *        "treegrid"
 *        "article"
 *        "columnheader"
 *        "definition"
 *        "directory"
 *        "document"
 *        "group"
 *        "heading"
 *        "img"
 *        "list"
 *        "listitem"
 *        "math"
 *        "note"
 *        "presentation"
 *        "region"
 *        "row"
 *        "rowgroup"
 *        "rowheader"
 *        "separator"
 *        "toolbar"
 *        "application"
 *        "banner"
 *        "complementary"
 *        "contentinfo"
 *        "form"
 *        "main"
 *        "navigation"
 *        "search"
 *      @prop [live] type={string} title="Aria-Live" description="Choose the value that defines how the screen-reader will present dynamically changing content of an element. It’s value can be set to off (the same as not using it at all), assertive (interrupts user actions to announce changes; example would be to recite the text for a hover link or button as it will recite and stop on and off hover), and polite (tells the screen reader to wait until the user is done to announce updates; example would be reciting the back of a flipcard so it waits until the user flips before reciting)."
 *        "assertive"
 *        "off"
 *        "polite"
 *      @prop label type={string} title="Aria-Label" description="Used to add accessible help or a description to an element. It will also override an element that already has a label assigned to it like a button. A use case would be adding a label when you have some kind of visual indication of an element's purpose, such as a button that uses a graphic instead of text."
 *      @prop labelledby type={string} title="Aria-Labelledby" description="Specifies the ID of another element in the DOM as an element's label. May also be used on any element, not just labelable elements. It overrides all other name sources for an element"
 *      @prop describedby type={string} title="Aria-Describedby" description="Use when there is some extra explanatory text that a user might need, whether it applies only to users of assistive technology or all users. Example is a password input field that is accompanied by some descriptive text explaining the minimum password requirements. It is a great way to communicate supplementary, but not essential, information and yet, it won't get in the way of more critical information such as the element's role."
 *    @end
 *  @prop @ignore additionalProperties type={string}
 */
import EF from 'ef/ef';
import View from 'views/base/view';
import BlocksView from 'views/block/block-collection-view';
import LocaleService from 'services/loader-handler-services/locale-service';
import ContentService from 'services/loader-handler-services/content-service';
import BlockOrderService from 'services/course-handler-services/block-order-service';
import AtomLoader from 'services/loader-handler-services/atom-loader';
import Chaplin from 'chaplin';
import _ from 'lodash';
import utils from 'lib/utils';

/* # BlockView
//
// Base view to be used with all views that will render a page.
// All blocks have the following properties:
// * type: defines which type of block to load
// * class: a class to apply to the div that will wrap around the block
//
// Conventions:
// * page.type: used to define file paths
// ** the page views themselves should be located at /views/page/*TYPE*\/*TYPE*-view
// ** all templates that might be rendered by the view should be located at /views/page/*TYPE*\/templates/
// ** the default template is expected at /views/page/*TYPE*\/templates/*TYPE*
// * page.template (optional): a template name other than the default
// ** if left undefined, the default template will be used
// ** must correspond to the name of another template in the templates folder for the view
// Initialization Flow and State Transitions:
// * Chaplin will call `initialize` where we hook `prePlay` to the `addedToDOM` event; `state: initialized`
// * `prePlay` calls load, which should be used by the page to add in DOM elements and such, but not to start anything playing; `state: loaded`
// * `prePlay` will also display the instructions, if available for the page.  Once the user is finished with them, `autoPlay` will be called.
//   If instructions are not available, `autoPlay` is called immediately.  `state: playing`
// * `play` should be on the pages themselves and is for actions when the page starts
// Methods for Inheriting Objects
// * load, play, mute, unmute, pause, resume, replay
*/
const BlockView = View.extend({
  autoRender: true,
  listen: {
    'action:pause mediator': 'pause',
    'action:resume mediator': 'resume',
    'action:mute mediator': 'mute',
    'action:unmute mediator': 'unmute',
  },
  initialize: function (options) {
    _.bindAll(this, 'readyToGo');
    this.atomArr = [];
    this.parent = options.parent;
    this.script = options.script;
    this.pageView = options.pageView;
    this.nestedLoads = [];

    if (this.pageView) {
      this.pageView.nested = [];
    }
    if (EF.forgeMode) {
      this.originalModel = _.cloneDeep(this.model.attributes);
      this.setForgeEvts();
    }

    // Allow Atoms ?
    this.atomsAllowed = false;

    //TODO: using this when rendering nested views; want this value available in hbs templates
    this.model.set('cid', this.model.cid);
    const self = this;
    //TODO: we're kinda tied to blocks never being rendered outside of a collection, confirm that's OK

    if (this.model.get('order') && this.pageView.model?.get('completed') && this.model.get('disable_order_on_replay')) {
      this.model.unset('order');
    }
    this.listenTo(this.parent, 'addedToDOM', function () {
      if (self.model.get('tabindex') || self.model.get('tabindex') === 0) {
        self.$el.attr('tabindex', self.model.get('tabindex'));
      }
      if (self.atomsAllowed && !self.atomsRendered) {
        self.renderAtomsFromTemplate();
      }
      self.load();
      self.applyAnimationProperty();
      self.applyAriaProperties();
      self.applyAttributeProperties();
      self.applyTabIndex();
      if (self.nestedLoads.length === 0) {
        if (self.model.get('state') === 'initialized') {
          self.setState('loaded');
        }
        self.trigger('block:state:loaded', self);
      } else {
        Promise.all(self.nestedLoads).then(() => {
          if (self.model.get('state') === 'initialized') {
            self.setState('loaded');
          }
          self.trigger('block:state:loaded', self);
        });
      }
    });
    this.listenTo(this.parent, 'page:state:change', this.onStateChange);
    this.setState('initialized');
    View.prototype.initialize.apply(this, arguments);
  },
  setForgeEvts() {
    const forgePath = this.model.get('forge_path') || this.model.get('objpath');
    if (!forgePath) {
      return;
    }
    if (this.model.get('content_region')) {
      this.$el[0].setAttribute('data-content-region', this.model.get('content_region'));
    }
    this.$el[0].setAttribute('data-forge-path', forgePath);

    if (this.model.get('type') === 'component') {
      this.$el[0].setAttribute('data-forge-type', this.model.get('path').split('/').reverse()[0]);
    } else {
      this.$el[0].setAttribute('data-forge-type', this.model.get('type'));
    }
    this.$el.on('mouseenter mouseleave mousedown mouseup contextmenu', evt => {
      if (this.model.get('type') === 'browser-course-editor') {
        evt.stopPropagation();
      } else {
        if (['mouseenter', 'mouseleave', 'contextmenu'].includes(evt.type)) {
          evt.preventDefault();
        }
        this.publishEvent(`forge:block:${evt.type}`, {
          forgeId: this.model.get('forgeId'),
          blockEvt: evt,
          $el: this.$el,
          modelObj: this.originalModel,
          pageX: evt.pageX,
          pageY: evt.pageY,
          theBlock: this,
          atomArr: this.atomArr,
        });
      }
    });
    if (this.originalModel['module-content']) {
      this.originalModel['module-content'][this.originalModel['module-content']['module-key']].atomType =
        this.originalModel['module-content']['module-key'];
      this.addAtomToList(this.originalModel['module-content'][this.originalModel['module-content']['module-key']]);
    }
  },
  renderMultipleNestedBlocks: function (arrayName) {
    const self = this;
    // add the nested item type to array for this page
    if (this.pageView && !this.pageView.nested.includes(arrayName)) {
      this.pageView.nested.push(arrayName);
    }
    _.each(this.model.get(arrayName), function (item, index) {
      self.renderNestedBlocks(`${self.model.get('cid')}-${arrayName}-${index}-blocks`, item.blocks);
    });
  },
  renderNestedBlocks: function (blockRegionName, blocks, templateData) {
    if (!blocks) {
      return;
    }

    this.registerRegion(blockRegionName, `[data-region="${blockRegionName}"]`);
    const nestedBlockPromise = ContentService.buildBlockCollection(blocks).then(blockCollection => {
      blockCollection.each(b => {
        if (this?.model?.get('order')) {
          const disableOrderOnReplay =
            this.pageView.model?.get('completed') && this.model.get('disable_order_on_replay');
          if (!disableOrderOnReplay) {
            b.set('order', this.model.get('order'));
          }
        }
      });

      const blocksViewOptions = {
        collection: blockCollection,
        region: blockRegionName,
        parent: this,
        autoAttach: this.autoAttach,
        script: this.script,
        pageView: this.pageView,
        templateData,
      };

      if (blocks.forge_path) {
        blocksViewOptions.collection.forge_path = blocks.forge_path;
      }

      const blocksView = new BlocksView(blocksViewOptions);
      this.listenTo(blocksView, 'block:state:loaded', function (block) {
        this.trigger('block:state:loaded', block);
      });
      this.listenTo(blocksView, 'block:state:completed', function (block) {
        this.trigger('block:state:completed', block);
        if (this.$el && _.every(this.subviews, sv => !(sv instanceof BlocksView) || sv.isComplete())) {
          this.$el.addClass('all-completed');
        }
      });
      this.subview(blockRegionName, blocksView);
    });
    this.nestedLoads.push(nestedBlockPromise);
    return nestedBlockPromise;
  },
  render() {
    this.atomsRendered = false;
    View.prototype.render.apply(this, arguments);
  },
  reRender: function () {
    this.render();
    if (this.script && this.script.reRender) {
      this.script.reRender();
    }
  },
  onStateChange: function (parent) {
    const parentState = parent.model.get('state');
    const order = this.model.get('order');
    const state = this.model.get('state');
    // don't push completed parent state on children
    // and prevent collisions of triggered events
    if (parentState !== 'completed' && state !== 'completed') {
      if (parentState === 'playing' && order) {
        if (order === BlockOrderService.getCurrentOrder()) {
          this.setState(parentState);
        }
      } else {
        this.setState(parentState);
      }
    } else if (parentState === 'completed' && state === 'loaded' && order === BlockOrderService.getCurrentOrder()) {
      this.setState('playing');
    }
    this.trigger('page:state:change', parent);
  },
  // Atoms render utilities
  createAtomRegion: function (region) {
    const regionId = `atom-${region.atom}-${region.cid}`;
    this.registerRegion(regionId, `#${region.cid}`);
    return regionId;
  },
  createAtomForRegion: function (config, regionId) {
    const atomConfig = AtomLoader.setAtomConfig(config, this.el);
    const atomClass = config.atom;
    atomConfig.attributes = AtomLoader.setAtomAttributes(config);
    const AtomView = new AtomLoader.atoms[atomClass]({
      region: regionId,
      model: new Chaplin.Model(atomConfig),
    });
    this.subview(regionId, AtomView);
    this.addAtomToList(AtomView);
  },
  addAtomToList: function (atomView) {
    this.atomArr.push(atomView);
  },
  renderAtomsFromTemplate: function () {
    const regions = AtomLoader.loadAtomRegions(this.el);
    if (regions.length > 0) {
      for (let r = 0; r < regions.length; r++) {
        const region = regions[r].get();
        const regionId = this.createAtomRegion(region);
        this.createAtomForRegion(region, regionId);
        regions[r].clean();
      }
      this.atomsRendered = true;
    } else {
      throw new Error('There are not [data-atom] elements. Consider disallowing Atoms on your block');
    }
  },
  //TODO: figure out how these interact with the page-view and actionbar
  load: function () {},
  play: function () {
    if (EF.options.local) {
      throw new Error('Play method should be implemented for all blocks');
    }
  },
  pause: function () {},
  resume: function () {},
  replay: function () {},
  reload: function () {},
  complete: function () {
    this.trigger('block:state:completed', this);
  },
  unload: function () {},
  mute: function () {},
  unmute: function () {},
  getTemplateFunction: function () {
    if (this.model.get('customTemplate')) {
      return require(this.model.get('customTemplate'));
    } else {
      return this.template;
    }
  },
  readyToGo: function () {
    this.setState('completed');
  },
  setState: function (newState) {
    if (!this.model) {
      return;
    }
    const oldState = this.model.get('state');
    this.model.set('state', newState);
    this.$el.removeClass(oldState).addClass(newState);

    const stateListeners = {
      playing: this.play,
      paused: this.pause,
      completed: this.complete,
      unloading: this.unload,
    };

    if (newState === 'playing' && oldState === 'paused') {
      this.resume();
    } else if (stateListeners[newState]) {
      _.bind(stateListeners[newState], this).call();
    }

    if (newState === 'playing' && oldState === 'loaded' && EF.enrollment.get('demoMode')) {
      this.readyToGo();
    }
  },
  isLoaded: function () {
    const selfLoaded = this.model.get('state') && this.model.get('state') !== 'initialized';
    if (selfLoaded && this.subviews) {
      return _.every(this.subviews, function (subview) {
        if (subview instanceof BlocksView) {
          return subview.isLoaded();
        } else {
          return true;
        }
      });
    } else {
      return selfLoaded;
    }
  },
  isComplete: function () {
    if (!this.model) {
      return;
    }
    const selfComplete = this.model.get('state') === 'completed';
    if (selfComplete && this.subviews) {
      return _.every(this.subviews, function (subview) {
        if (subview instanceof BlocksView) {
          return subview.isComplete();
        } else {
          return true;
        }
      });
    } else {
      return selfComplete;
    }
  },
  applyAnimationProperty: function () {
    utils.applyAnimationProperty(this);
  },
  applyAriaProperties: function () {
    if (this.model.get('aria')) {
      Object.keys(this.model.get('aria')).forEach(key => {
        const value = this.model.get('aria')[key];
        let prefix = '';
        if (key !== 'role') {
          prefix = 'aria-';
        }
        this.$el.attr(prefix + key, LocaleService.t(value));
      });
    }
  },

  renderChildAriaProperties: function (childItem, i, parentEl = this.$el) {
    if (Object.prototype.hasOwnProperty.call(childItem, 'aria')) {
      Object.keys(childItem.aria).forEach(itemKey => {
        const keyVal = childItem.aria[itemKey];
        let prefix = 'aria-';
        if (itemKey === 'role') {
          prefix = '';
        }
        parentEl
          .children()
          .eq(i)
          .attr(prefix + itemKey, LocaleService.t(keyVal));
      });
    }
  },

  /**
   * Applies an arbitrary collection of attributes to any block
   *
   * Notes:
   *   A value can be provided and the selector to find the element will default to [data-attributable]
   *
   *   An object containing a selector and a value can be provided for more control
   *
   *   The keys that describe the attribute name can be deeply nested and will be separated by dashes
   *     Example: { data: { test: { one: { two: 2}}} => data-test-one-two="2"
   *
   * Example:
   *   - type: text
   *     items:
   *       - description: some text
   *     attributes:
   *       data:
   *         value: 10              #=> yields data-value="10" with selector [data-attributable]
   *         info:
   *           - selector: '.item'  #=> yields data-info="10" with selector .item
   *             value: 10
   *       aria:
   *         label: some text       #=> yields aria-label="some text" with selector [data-attributable]
   */
  applyAttributeProperties: function () {
    let attributes;
    // attributes in fields are deeply nested, that's why we are doing this
    if (this.subviewsByName.field && this.subviewsByName.field.model) {
      const fieldModel = this.subviewsByName.field.model;
      // this is for choice fields
      const optionAttributes = _.find(fieldModel.get('options'), 'attributes');
      // all other fields
      const fieldAttributes = fieldModel.get('attributes');
      attributes = optionAttributes ? _.get(optionAttributes, 'attributes') : fieldAttributes;
    } else {
      attributes = this.model.get('attributes');
    }

    if (attributes) {
      attributes = _.cloneDeep(attributes);
      const attrArray = (function flatten(attrs, path) {
        return Array.prototype.concat(
          ...Object.entries(attrs).map(([key, value]) => {
            if (Array.isArray(value)) {
              return value.map(v => ({
                attribute: `${path}-${key}`,
                value: _.get(v, 'value'),
                selector: _.get(v, 'selector'),
              }));
            } else if (typeof value === 'object') {
              return flatten(value, path ? `${path}-${key}` : key);
            } else {
              return {
                attribute: path ? `${path}-${key}` : `${key}`,
                value,
                selector: '[data-attributable]',
              };
            }
          })
        );
      })(attributes);

      attrArray.forEach(info => {
        const { selector, attribute, value } = info;
        if (selector && attribute && value) {
          this.$el.find(selector).attr(attribute, value);
        }
      });
      this.$el.find('*').removeAttr('data-attributable');
    }
  },

  applyTabIndex: function () {
    if (this.model.get('tabindex')) {
      this.$el.attr('tabindex', this.model.get('tabindex'));
    }
  },
});

export default BlockView;
